import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClaimTimeline from "@/components/ClaimTimeline";
import LiveClaimTracker from "@/components/LiveClaimTracker";
import SurveyorTracker from "@/components/SurveyorTracker";
import VideoCallButton from "@/components/VideoCallButton";
import ClaimPhotoGallery from "@/components/ClaimPhotoGallery";
import ClaimChat from "@/components/ClaimChat";
import DamageEstimator from "@/components/DamageEstimator";
import MultiPhotoDamageEstimator from "@/components/MultiPhotoDamageEstimator";
import { ArrowLeft, FileText, Sparkles, Phone, CheckCircle2 } from "lucide-react";

export default function ClaimDetail() {
  const { claimId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(!!location.state?.justCreated);

  useEffect(() => {
    Promise.all([
      api.get(`/claims/${claimId}`).then((r) => setClaim(r.data)),
      api.get(`/documents`, { params: { claim_id: claimId } }).then((r) => setDocs(r.data)).catch(() => []),
    ]).catch(() => setClaim(null)).finally(() => setLoading(false));
  }, [claimId]);

  if (loading) {
    return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#003DA5] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!claim) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-center">
        <div>
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-800">Claim not found</p>
          <Link to="/dashboard" className="text-sm text-[#003DA5] underline mt-2 inline-block">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] py-10" data-testid="claim-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#003DA5] mb-4" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        {showSuccess && (
          <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-[#00A86B] to-[#10B981] text-white flex items-center gap-4" data-testid="claim-success-banner">
            <CheckCircle2 className="w-10 h-10 shrink-0" />
            <div className="flex-1">
              <div className="font-heading font-bold text-lg">Claim filed successfully!</div>
              <div className="text-sm opacity-90">Your Claim Manager will reach out within 30 minutes. Save your Claim ID: <span className="font-mono font-bold">{claim.claim_id}</span></div>
            </div>
            <button onClick={() => setShowSuccess(false)} className="text-white/80 hover:text-white text-sm font-semibold">Dismiss</button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <LiveClaimTracker claim={claim} timeline={claim.timeline} />
            <VideoCallButton claim={claim} />
            <ClaimChat claim={claim} embedded />
            <ClaimPhotoGallery claim={claim} />
            <SurveyorTracker claim={claim} />
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Claim ID</div>
                  <div className="font-mono font-extrabold text-2xl text-slate-900">{claim.claim_id}</div>
                </div>
                <span className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#E6F0FF] text-[#003DA5]">
                  {claim.current_status}
                </span>
              </div>
              <div className="text-sm text-slate-500 mt-1">Filed {new Date(claim.created_at).toLocaleString()}</div>

              <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                <Detail label="Vehicle" value={`${claim.vehicle.make} ${claim.vehicle.model} (${claim.vehicle.year})`} />
                <Detail label="Reg. No." value={claim.vehicle.reg_no} mono />
                <Detail label="Insurer" value={claim.insurance.insurer} />
                <Detail label="Policy" value={claim.insurance.policy_no} mono />
                <Detail label="Accident Date" value={claim.accident.date} />
                <Detail label="Location" value={claim.accident.location} />
                <div className="sm:col-span-2">
                  <Detail label="What happened" value={claim.accident.description} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Documents ({docs.length})</h2>
              {(() => {
                const accidentPhotos = docs.filter(d => d.category === "Accident Photo" && (d.content_type || "").startsWith("image/"));
                if (accidentPhotos.length >= 2) {
                  return (
                    <div className="mb-5">
                      <MultiPhotoDamageEstimator
                        photos={accidentPhotos}
                        pincode={claim.customer?.pincode}
                        insurer={claim.insurance?.insurer ? claim.insurance.insurer.toLowerCase().replace(/\s+/g, "-") : null}
                        claimId={claim.claim_id}
                        customerMobile={claim.customer?.mobile}
                        customerName={claim.customer?.name}
                      />
                    </div>
                  );
                }
                return null;
              })()}
              {docs.length === 0 ? (
                <p className="text-sm text-slate-500">No documents yet. <Link to="/vault" className="text-[#003DA5] underline">Upload from vault</Link></p>
              ) : (
                <ul className="grid sm:grid-cols-2 gap-2">
                  {docs.map((d) => (
                    <li key={d.doc_id} className="p-3 rounded-xl border border-slate-100 bg-slate-50" data-testid={`claim-doc-${d.doc_id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#003DA5] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{d.original_filename}</div>
                          <div className="text-xs text-slate-500">{d.category}</div>
                        </div>
                      </div>
                      {d.category === "Accident Photo" && (d.content_type || "").startsWith("image/") && (
                        <DamageEstimator
                          docId={d.doc_id}
                          pincode={claim.customer?.pincode}
                          insurer={claim.insurance?.insurer ? claim.insurance.insurer.toLowerCase().replace(/\s+/g, "-") : null}
                          claimId={claim.claim_id}
                          customerMobile={claim.customer?.mobile}
                          customerName={claim.customer?.name}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Timeline log</h2>
              <ClaimTimeline timeline={claim.timeline} currentStatus={claim.current_status} />
            </div>

            <div className="bg-gradient-to-br from-[#003DA5] to-[#0050D1] rounded-2xl p-6 text-white">
              <Sparkles className="w-6 h-6 mb-2" />
              <div className="font-heading font-bold text-base">Your Claim Manager</div>
              <p className="text-sm opacity-90 mt-1">Assigned shortly. We'll WhatsApp & email you their details.</p>
              <a href="tel:+919942249388" className="mt-4 inline-flex items-center gap-2 bg-white text-[#003DA5] rounded-lg px-4 py-2 text-sm font-semibold">
                <Phone className="w-4 h-4" /> Call +91 99422 49388
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-900 font-semibold mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
